import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'

import { ApiError, sessionApi } from './api'
import { clearCsrfToken } from './csrf'
import { queryKeys } from './queryClient'
import type { MembershipSummary, SessionResponse, User, UUID } from './types'

export type SessionStatus = 'loading' | 'authenticated' | 'anonymous' | 'error'

export interface SessionValue {
  status: SessionStatus
  user: User | null
  organizations: MembershipSummary[]
  activeOrganizationId: UUID | null
  /** The workspace a bare /app URL should land in. */
  defaultOrganization: MembershipSummary | null
  /**
   * True when this account administers the whole deployment (`users.is_admin`).
   *
   * Used only to decide whether the admin console's entry point is rendered.
   * The console's routes answer 404 to everyone without the flag, so this is a
   * rendering hint and never an authorisation.
   */
  isPlatformAdmin: boolean
  /** The server can transcribe recordings with its Whisper engine. */
  voiceTranscription: boolean
  /** Populated only when the session could not be read at all (network, 5xx). */
  error: unknown
  refresh: () => Promise<void>
  /** Seeds the cache from a login or register response — no extra round trip. */
  adopt: (session: SessionResponse) => void
  signOut: () => Promise<void>
}

const SessionContext = createContext<SessionValue | null>(null)

/**
 * Loads the cookie session once and keeps it in the query cache.
 *
 * An unauthenticated visitor is a normal state, not an error, so the query maps
 * a 401 to `null` rather than throwing. That distinction matters: `null` means
 * "sign in", while a thrown error means the API is unreachable, and showing a
 * login form in the second case would look like the account had been lost.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient()

  const query = useQuery<SessionResponse | null>({
    queryKey: queryKeys.session,
    queryFn: async ({ signal }) => {
      try {
        return await sessionApi.current({ signal })
      } catch (error) {
        if (error instanceof ApiError && error.isUnauthenticated) return null
        throw error
      }
    },
    // The session is the app's root dependency: refetch it rarely, and never
    // let a stale copy outlive a sign-out in another tab for long.
    staleTime: 60_000,
    retry: false,
  })

  // A 401 from any other request means the cookie died mid-session (expired,
  // revoked, or signed out elsewhere). Drop the cached session so the router
  // redirects, instead of leaving a signed-in shell around a dead credential.
  //
  // Both caches are watched: a read failing is the common case, but a write
  // failing is the one where leaving a dead shell in place would let someone
  // keep typing into a form that can never submit.
  useEffect(() => {
    function expire() {
      clearCsrfToken()
      queryClient.setQueryData(queryKeys.session, null)
    }
    function isExpired(error: unknown) {
      return error instanceof ApiError && error.isUnauthenticated
    }

    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated') return
      // The session query maps its own 401 to null; reacting to it here would
      // be a loop.
      if (event.query.queryKey[0] === queryKeys.session[0]) return
      if (isExpired(event.query.state.error)) expire()
    })
    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== 'updated') return
      if (isExpired(event.mutation?.state.error)) expire()
    })

    return () => {
      unsubscribeQueries()
      unsubscribeMutations()
    }
  }, [queryClient])

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.session })
  }, [queryClient])

  const adopt = useCallback(
    (session: SessionResponse) => {
      queryClient.setQueryData(queryKeys.session, session)
    },
    [queryClient],
  )

  const signOut = useCallback(async () => {
    try {
      await sessionApi.logout()
    } finally {
      clearCsrfToken()
      // Everything in the cache belonged to the account that just left.
      queryClient.clear()
      queryClient.setQueryData(queryKeys.session, null)
    }
  }, [queryClient])

  const value = useMemo<SessionValue>(() => {
    const data = query.data ?? null
    let status: SessionStatus = 'loading'
    if (query.isError) status = 'error'
    else if (data) status = 'authenticated'
    else if (!query.isPending) status = 'anonymous'

    const organizations = data?.organizations ?? []
    const activeOrganizationId = data?.active_organization_id ?? null
    const defaultOrganization =
      organizations.find((org) => org.id === activeOrganizationId) ??
      organizations.find((org) => org.is_default) ??
      organizations[0] ??
      null

    return {
      status,
      user: data?.user ?? null,
      organizations,
      activeOrganizationId,
      defaultOrganization,
      isPlatformAdmin: data?.features?.platform_admin === true,
      voiceTranscription: data?.features?.voice_transcription === true,
      error: query.error,
      refresh,
      adopt,
      signOut,
    }
  }, [query.data, query.error, query.isError, query.isPending, refresh, adopt, signOut])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext)
  if (!value) {
    throw new Error('useSession must be used inside <SessionProvider>')
  }
  return value
}

/**
 * Sign-in and sign-up, sharing one code path so both seed the session cache
 * the same way and land the user in the same place.
 */
export function useLogin() {
  const { adopt } = useSession()
  return useMutation({
    // `remember` is the sign-in form's "Keep me signed in" box. It reaches the
    // API, which shortens both the session row and the cookies when it is false.
    mutationFn: (input: { email: string; password: string; remember?: boolean }) =>
      sessionApi.login(input),
    onSuccess: adopt,
  })
}

export function useRegister() {
  const { adopt } = useSession()
  return useMutation({
    mutationFn: (input: {
      name: string
      email: string
      password: string
      password_confirmation: string
    }) => sessionApi.register(input),
    onSuccess: adopt,
  })
}
